import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API_BASE, getToken, saveToken, dropToken } from "../../lib/admin";

const ease = [0.16, 1, 0.3, 1];

/**
 * AdminGate — the single authentication gate for every /admin/* surface.
 *
 * Verifies the session-held token against GET /api/admin/verify and renders
 * children only when the token is valid. Keeps the private surface out of
 * search indexes (noindex) and shows the small private admin navigation
 * (Inbox · Content · Sign out) once authenticated.
 */
export default function AdminGate({ title, children }) {
    const [phase, setPhase] = useState(() => (getToken() ? "checking" : "gate"));
    const [tokenInput, setTokenInput] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const originalTitle = useRef(document.title);

    useEffect(() => {
        document.title = "Private — Life is a Project";
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow";
        document.head.appendChild(meta);
        return () => {
            document.title = originalTitle.current;
            if (meta.parentNode) meta.parentNode.removeChild(meta);
        };
    }, []);

    useEffect(() => {
        if (phase !== "checking") return;
        let cancelled = false;
        fetch(`${API_BASE}/admin/verify`, { headers: { "X-Admin-Token": getToken() } })
            .then((r) => { if (!cancelled) setPhase(r.ok ? "ready" : "gate"); })
            .catch(() => { if (!cancelled) setPhase("gate"); });
        return () => { cancelled = true; };
    }, [phase]);

    const submit = async (e) => {
        e.preventDefault();
        const token = tokenInput.trim();
        if (!token || busy) return;
        setBusy(true);
        setError(null);
        try {
            const resp = await fetch(`${API_BASE}/admin/verify`, { headers: { "X-Admin-Token": token } });
            if (resp.ok) {
                saveToken(token);
                setTokenInput("");
                setPhase("ready");
            } else if (resp.status === 401) {
                setError("Invalid token.");
            } else if (resp.status === 503) {
                setError("The admin area is not enabled on this server.");
            } else if (resp.status === 429) {
                setError("Too many attempts. Please wait a few minutes and try again.");
            } else {
                setError("Unexpected response. Please try again.");
            }
        } catch (_e) {
            setError("Network error — the server could not be reached.");
        } finally {
            setBusy(false);
        }
    };

    const signOut = () => {
        dropToken();
        setPhase("gate");
        setError(null);
    };

    return (
        <main
            id="main"
            data-testid="admin-page"
            className="min-h-screen bg-[color:var(--ivory)] text-[color:var(--navy)] relative"
        >
            <div className="max-w-[1024px] mx-auto px-6 md:px-10 py-16 md:py-24">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease }}
                    className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--navy)] mb-6 flex items-center gap-4"
                >
                    <span aria-hidden className="gold-thin" />
                    Private — Internal
                </motion.div>
                <motion.h1
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease, delay: 0.05 }}
                    className="font-display font-light tracking-[-0.015em] leading-[1.02]"
                    style={{ fontSize: "clamp(36px, 5vw, 64px)" }}
                >
                    {title}
                </motion.h1>

                {phase === "ready" && (
                    <nav
                        data-testid="admin-nav"
                        aria-label="Admin"
                        className="mt-8 flex items-center gap-5 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                    >
                        <Link to="/admin/inbox" data-testid="admin-nav-inbox" className="link-editorial hover:text-[color:var(--navy)]">Inbox</Link>
                        <span aria-hidden>·</span>
                        <Link to="/admin" data-testid="admin-nav-content" className="link-editorial hover:text-[color:var(--navy)]">Content</Link>
                        <span aria-hidden>·</span>
                        <button type="button" data-testid="admin-signout-button" onClick={signOut} className="link-editorial uppercase tracking-[0.24em]">
                            Sign out
                        </button>
                    </nav>
                )}

                {phase === "ready" ? (
                    children
                ) : phase === "checking" ? (
                    <p className="mt-12 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                        Verifying…
                    </p>
                ) : (
                    <form
                        onSubmit={submit}
                        data-testid="admin-gate-form"
                        className="mt-12 max-w-[420px] border-t border-[color:var(--line-strong)] pt-8"
                    >
                        <label
                            htmlFor="admin-token"
                            className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] block mb-3"
                        >
                            Admin token
                        </label>
                        <input
                            id="admin-token"
                            data-testid="admin-token-input"
                            type="password"
                            autoComplete="off"
                            value={tokenInput}
                            onChange={(e) => setTokenInput(e.target.value)}
                            placeholder="Enter the admin token"
                            className="w-full bg-transparent border border-[color:var(--line-strong)] px-4 py-3 text-[15px] text-[color:var(--navy)] placeholder:text-[color:var(--warm-gray-2)]/70 focus:outline-none focus:border-[color:var(--gold)] transition-colors"
                        />
                        {error && (
                            <p data-testid="admin-gate-error" role="alert" className="mt-3 text-[13px] text-[#9A3B26]">
                                {error}
                            </p>
                        )}
                        <button type="submit" data-testid="admin-signin-button" disabled={busy} className="btn-editorial mt-6">
                            {busy ? "Verifying…" : "Continue"}
                            <span aria-hidden className="w-4 h-px bg-current opacity-70" />
                        </button>
                        <p className="mt-8 text-[12px] leading-[1.65] text-[color:var(--warm-gray-2)] max-w-[46ch]">
                            Access is limited to the site administrator. The token is kept only for this
                            browser tab and is never stored on the site itself.
                        </p>
                    </form>
                )}
            </div>
        </main>
    );
}
